import React, { createContext, useCallback, useState } from 'react'
import { getLogger } from '@deltachat-desktop/shared/logger'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { type runtime } from '@deltachat-desktop/runtime-interface'
import { positionStore } from '../components/AudioPlayer/positionStore'

const log = getLogger('MediaPlayerMutexContext')

export type MediaPlayerMutexContextValue = {
  /**
   * This never changes
   */
  audioElement: HTMLAudioElement
  /**
   * The string is provided in the form returned from
   * {@linkcode runtime.transformBlobURL}.
   */
  currentSrc: string | null
  eventListeners: {
    onPlay: React.ReactEventHandler<HTMLMediaElement>
    onPause: React.ReactEventHandler<HTMLMediaElement>
    onSeeking: React.ReactEventHandler<HTMLMediaElement>
    onRateChange: React.ReactEventHandler<HTMLMediaElement>
    onVolumeChange: React.ReactEventHandler<HTMLMediaElement>
  }
  /**
   * Basically the same as
   * {@linkcode MediaPlayerMutexContextValue.eventListeners['onPlay']},
   * but takes only `src` instead of an entire event.
   */
  play: (src: string) => void
  stop: () => void
  /**
   * Global playback rate, shared by all voice/audio message bubbles so they
   * agree. Applied to the singleton element that actually produces sound.
   */
  playbackRate: number
  setPlaybackRate: (rate: number) => void
}

const audioEl = document.createElement('audio')
audioEl.controls = true
// Clear a remembered voice-message position once it plays to its natural end —
// on the singleton, so it fires even if the bubble unmounted mid-play (playback
// continues here across chat switches). src-keyed, matching positionStore.
// No-ops for absent keys (setting off / non-voice audio).
audioEl.addEventListener('ended', () => {
  const src = audioEl.getAttribute('src')
  if (src != null && src !== '') {
    positionStore.clearPosition(src)
  }
})

const noContextErrStr =
  'tried to use MediaPlayerMutexContextValue outside of context provider'
/**
 * A context that facilitates ensuring that only one media element
 * (voice message, etc) plays at a time.
 */
export const MediaPlayerMutexContext =
  createContext<MediaPlayerMutexContextValue>({
    audioElement: audioEl,
    currentSrc: null,
    eventListeners: {
      onPlay: () => {
        log.warn(noContextErrStr)
      },
      onPause: () => {
        log.warn(noContextErrStr)
      },
      onSeeking: () => {
        log.warn(noContextErrStr)
      },
      onRateChange: () => {
        log.warn(noContextErrStr)
      },
      onVolumeChange: () => {
        log.warn(noContextErrStr)
      },
    },
    play: () => {
      log.warn(noContextErrStr)
    },
    stop: () => {
      log.warn(noContextErrStr)
    },
    playbackRate: 1,
    setPlaybackRate: () => {
      log.warn(noContextErrStr)
    },
  })

export function MediaPlayerMutexProvider({
  children,
}: React.PropsWithChildren<{}>) {
  const [currentSrc, setCurrentSrc] = useState<string | null>(null)
  const [playbackRate, setPlaybackRateState] = useState(1)

  const setPlaybackRate = useCallback((rate: number) => {
    setPlaybackRateState(rate)
    audioEl.playbackRate = rate
    // Loading a new `src` resets `playbackRate` to `defaultPlaybackRate`
    // (per the HTML media load algorithm / MDN), so set both here to make
    // the chosen rate survive track changes / auto-advance.
    audioEl.defaultPlaybackRate = rate
  }, [])

  const setSrcAndPlay = useCallback((newSrc: string | null) => {
    setCurrentSrc(newSrc)

    const newSrcStr = newSrc ?? ''
    if (audioEl.getAttribute('src') !== newSrcStr) {
      audioEl.src = newSrcStr
    }
    audioEl.play()
  }, [])

  return (
    <MediaPlayerMutexContext.Provider
      value={{
        audioElement: audioEl,
        currentSrc,
        eventListeners: {
          // Be careful with `HTMLMediaElement.src` comparisons.
          // `src`'s getter might return a value different from the one you set.
          // Namely, for `.src = '/home/me/vid.mp4`
          // it will be a `file:///...` URL.
          // `getAttribute('src')` returns the value that was provided
          // to the setter, which is what we should always be using
          // for comparisons.
          onPlay: e => {
            const el = e.currentTarget
            const newSrc = el.getAttribute('src')
            const srcChanged = audioEl.getAttribute('src') !== (newSrc ?? '')
            setSrcAndPlay(newSrc)
            // Carry a restored/scrubbed local position onto the singleton,
            // which otherwise always starts a freshly-loaded src at 0 (A2
            // remembered-position). Only when the src actually changed —
            // resuming the same src keeps the singleton's own position.
            // (The seek is applied once the new src reaches metadata.)
            if (
              srcChanged &&
              Number.isFinite(el.currentTime) &&
              el.currentTime > 0
            ) {
              audioEl.currentTime = el.currentTime
            }
          },
          onPause: e => {
            if (e.currentTarget.getAttribute('src') !== currentSrc) {
              return
            }
            if (e.currentTarget.ended) {
              // The media probably got paused simply because it ended.
              // Let's not manually pause and just let it end naturally,
              // to make sure that we fully play it through.
              return
            }
            audioEl.pause()
          },
          onSeeking: e => {
            if (e.currentTarget.getAttribute('src') !== currentSrc) {
              return
            }
            audioEl.currentTime = e.currentTarget.currentTime
          },
          onRateChange: e => {
            if (e.currentTarget.getAttribute('src') !== currentSrc) {
              return
            }

            audioEl.playbackRate = e.currentTarget.playbackRate
          },
          onVolumeChange: e => {
            if (e.currentTarget.getAttribute('src') !== currentSrc) {
              return
            }

            audioEl.volume = e.currentTarget.volume
            audioEl.muted = e.currentTarget.muted
          },
        },
        play: setSrcAndPlay,
        stop: useCallback(() => {
          setCurrentSrc(null)
          audioEl.src = ''
        }, []),
        playbackRate,
        setPlaybackRate,
      }}
    >
      {children}
    </MediaPlayerMutexContext.Provider>
  )
}
